<template>
    <IdeoFormGroup :label="$t('[[[Wybierz autora]]]')">
        <div class="d-flex flex-column flex-md-row">
            <div class="flex-grow-1">
                <IdeoAutocomplete
                    v-model="authorId"
                    name="authorId"
                    track-by="id"
                    :placeholder="$t('[[[zacznij pisać aby wyszukać autora...]]]')"
                    :fetch="(id: number) => fetchAuthor(id)"
                    :search="(query: string, limit: number) => searchAuthor(query, limit)"
                    :refresh="true"
                >
                    <template #selected="{ option }">
                        <div class="d-flex flex-nowrap align-items-center">
                            <IdeoImg
                                :src="$filters.image(option.facePictureUrl, 'ico')"
                                rounded="circle"
                                :blank="!option.facePictureUrl"
                                width="18"
                                height="18"
                                blank-color="#777"
                            />
                            <b class="ms-2">{{ option.userName }}</b>
                        </div>
                    </template>
                    <template #option="{ option }">
                        <div class="d-flex flex-nowrap align-items-center">
                            <IdeoImg
                                :src="$filters.image(option.facePictureUrl, 'ico')"
                                rounded="circle"
                                :blank="!option.facePictureUrl"
                                width="36"
                                height="36"
                                blank-color="#777"
                            />
                            <b class="ms-2">{{ option.userName }}</b>
                        </div>
                    </template>
                </IdeoAutocomplete>
            </div>
            <div class="d-flex align-items-stretch">
                <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addAuthor()">
                    <i class="fas fa-plus" />
                </button>
            </div>
        </div>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Cechy w grupie]]]')">
        <div class="list-group">
            <div v-for="item in authors" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                <div class="col-md-11 d-flex align-items-center">
                    <span class="me-2">
                        <i class="fad fa-fw fa-browser text-primary" />
                    </span>
                    <span class="me-auto">
                        <span class="d-block" :title="$t('[[[Autor]]]')">
                            <IdeoImg
                                :src="$filters.image(item.facePictureUrl, 'ico')"
                                rounded="circle"
                                :blank="!item.facePictureUrl"
                                width="18"
                                height="18"
                                blank-color="#777"
                            />
                            <b class="ms-2">{{ item.userName }}</b>
                        </span>
                    </span>
                </div>
                <span class="d-flex align-items-center">
                    <IdeoButton :id="`btn-delete-related-${item.id}`" variant="danger" size="sm" pill :title="$t('[[[Usuń autora]]]')">
                        <i class="fas fa-trash-alt" />
                    </IdeoButton>
                    <Confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-related-${item.id}`" :value="item.id" @confirm="removeAuthor(item.id)" />
                </span>
            </div>
            <div v-if="authors.length == 0" class="list-group-item list-group-item-light text-center">
                {{ $t('[[[Brak autorów]]]') }}
            </div>
        </div>
    </IdeoFormGroup>
</template>

<script lang="ts" setup>
import { computedAsync } from '@vueuse/core';

import type { ListItemModel } from '#layers/cms/app/pages/cms/modules/authors/_runtime/services/AuthorsService';
import AuthorsService from '#layers/cms/app/pages/cms/modules/authors/_runtime/services/AuthorsService';
import { useWebsitesStore } from '#layers/cms/app/pages/cms/management/websites/_runtime/stores/websites';

defineOptions({
    name: 'WidgetAuthorsSettings'
});

const { $log } = useLogging();

const { $alert } = useAlerts();

const websiteStore = useWebsitesStore();

interface FormSettings
{
    settings: {
        authorsIds: number[];
    };
}

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    authorsIds: []
};

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = { ...defaults, ...form.value.settings };
});

const authorId = ref<number>(null);
const authors = computedAsync(async () =>
{
    try
    {
        return await AuthorsService.getListByIds(form.value.settings.authorsIds);
    }
    catch (ex)
    {
        return [];
    }
});

const fetchAuthor = async (id: number): Promise<ListItemModel> =>
{
    const response = await AuthorsService.fetchAutocompleteModel(id);

    return response || null;
};

const searchAuthor = async (query: string, limit: number): Promise<ListItemModel[]> =>
{
    return await AuthorsService.getAutocompleteList(
        query,
        limit,
        websiteStore.current.website,
        websiteStore.current.locale,
        form.value.settings.authorsIds);
};

const addAuthor = async (): Promise<void> =>
{
    if (authorId.value == null)
        return;

    try
    {
        form.value.settings.authorsIds.push(authorId.value);
        authorId.value = null;
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
};

const removeAuthor = async (auhtorId: number): Promise<void> =>
{
    form.value.settings.authorsIds = form.value.settings.authorsIds.filter(x => x != auhtorId);
};
</script>
